class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.config = {
      dom: {
        cart_items: 'cart-items',
        cart_drawer_items: 'cart-drawer-items',
        cart_item_modal: 'cartItemModal',
        cart_item: 'js-cart-item',
        cart_custom_artwork: 'js-custom-artwork',
        setup_fee: 'js-setup-fee',
      },
    };

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const $itemModal = document.getElementById(`${this.config.dom.cart_item_modal}-${this.dataset.index}`);
      const cartItems = this.closest(this.config.dom.cart_items)
        || this.closest(this.config.dom.cart_drawer_items);

      if (!cartItems || !cartItems?.updateQuantity) {
        console.error('Cart items or updateQuantity function not found.');
        return;
      }

      // For Cart Drawer
      if ($itemModal) {
        const utils = SDG?.cartUtils() || {};
        utils?.initCartItemModal({
          modal: $itemModal,
          trigger: this.querySelector('button'),
          cb: async () => {
            await cartItems?.updateQuantity(this.dataset.index, 0);
          },
        });
        return;
      }

      // For Cart page
      // Fallback if no modal or remove button found
      await cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.config = {
      dom: {
        cart_item: '.js-cart-item',
        cart_item_acc: '.js-cart-item-acc',
        cart_checkout_button: 'CartDrawer-Checkout',
      },
    };
    this.cartItems = this.querySelectorAll(this.config.dom.cart_item) || [];
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });

    if (this.cartItems?.length > 0) {
      this.cartItems?.forEach((item) => {
        this.initAccordion(item);
      });
      // On page load only render already-cached messages; never fetch (fetching
      // happens on cart change). See updateQuantity / onCartUpdate.
      this.updateCartItemsDeliveryMessage(false);
    }
    const utils = SDG?.cartUtils() || {};
    if (utils) {
      utils?.fetchGeoLoaction();
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = document.getElementById(`Quantity-${id}`) || document.getElementById(`Drawer-quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  // Show the error inline (same red text as the PDP) instead of the browser's native bubble.
  setLineError(index, message) {
    const $texts = document.querySelectorAll(`#Line-item-error-${index} .cart-item__error-text`);
    $texts.forEach(($text) => { $text.textContent = message; });
    clearTimeout(this.lineErrorTimer);
    // Clear after 3 seconds, same as updateLiveRegions.
    if (message) this.lineErrorTimer = setTimeout(() => $texts.forEach(($text) => { $text.textContent = ''; }), 3000);
    return $texts.length > 0;
  }

  setValidity(event, index, message) {
    if (!this.setLineError(index, message)) {
      event.target.setCustomValidity(message);
      event.target.reportValidity();
    }
    this.resetQuantityInput(index);
    event.target.select();
  }

  getTextStyle(event) {
    const siteBranding = SDG?.Data?.siteBranding?.toLowerCase() || '';

    if (siteBranding === 'coverstore') {
      return '';
    }

    let props = [];
    try {
      props = JSON.parse(event.target.dataset.properties);
    } catch (e) {
      console.error('Invalid properties JSON:', e, event.target.dataset.properties);
      props = [];
    }

    const textStyleEntry = props.find(([key]) => key === 'Text Style');

    if (textStyleEntry) {
      // "Trapunto +$150" -> "Trapunto"
      return textStyleEntry[1].split('+')[0].trim();
    }

    return '';
  }

  /**
   * Trapunto is made at QD-Semi only, which US `total_qty` excludes (ITT-16655), so a trapunto
   * line is governed by the API's `can_be_trapunto` alone (see validateQuantityApi).
   */
  isTrapuntoStyle(style) {
    const canBe = (SDG?.Data?.personalizationOptionCanBeMap || {})[style];
    return canBe === 'can_be_trapunto' || String(style || '').toLowerCase().includes('trapunto');
  }

  /**
   * Quantity of this SKU the current customer may buy, per the stock-info response
   * (`total_qty`), or null when the payload has none (error body, legacy API).
   */
  getSellableQty(result) {
    const totalQty = Number(result?.total_qty);
    return Number.isFinite(totalQty) ? totalQty : null;
  }

  /**
   * Whether a cart line's quantity input belongs to a trapunto line ("Trapunto" in its
   * Text Style property; `data-properties` is the line's properties JSON).
   */
  isTrapuntoLine(input) {
    let props = [];
    try {
      props = JSON.parse(input?.dataset?.properties || '[]');
    } catch (e) {
      return false;
    }
    const textStyle = Array.isArray(props)
      ? (props.find(([key]) => key === 'Text Style') || [])[1]
      : props?.['Text Style'];
    return String(textStyle || '').toLowerCase().includes('trapunto');
  }

  /**
   * Quantity of the same SKU held by the OTHER cart lines of this line's pool (server-rendered
   * values). The pools are validated separately: trapunto lines (QD-Semi capacity, judged by
   * can_be_trapunto) versus everything else (total_qty). Pass trapuntoLines=true to sum the
   * trapunto pool.
   */
  getOtherLinesQty(input, sku, trapuntoLines = false) {
    if (!sku) return 0;
    return [...this.querySelectorAll('input.quantity__input[data-sku]')]
      .filter((el) => el !== input && el.dataset.sku === sku
        && this.isTrapuntoLine(el) === trapuntoLines)
      .reduce((sum, el) => sum + (parseInt(el.getAttribute('value'), 10) || 0), 0);
  }

  validateQuantityApi(style, result, qty) {
    // `can_be_trapunto` (evaluated by the API for this line's qty) is the whole answer for
    // trapunto — total_qty does not cover QD-Semi capacity.
    if (this.isTrapuntoStyle(style)) {
      return String(result?.can_be_trapunto || '').toLowerCase() === 'yes';
    }

    // Mirror the PDP (ProductConfigurables.computeIsValidInventory): `total_qty` is the only
    // field that encodes what THIS customer may buy — for US it is Carr-FG (+ in-transit) only
    // (ITT-16655). `message` is still composed over every location tier (QD-Semi MTO included),
    // so an over-qty US request answers "made to order…" rather than "Out of stock", and a
    // message-only check lets it through.
    const sellableQty = this.getSellableQty(result);
    if (sellableQty !== null && Number.isFinite(qty) && qty > sellableQty) {
      return false;
    }

    const siteBranding = SDG?.Data?.siteBranding?.toLowerCase() || '';

    if (siteBranding === 'leatherology') {
      const optionCanBeMap = SDG?.Data?.personalizationOptionCanBeMap || {};
      const canBe = optionCanBeMap[style];
      if (canBe && (canBe in result)) {
        return result[canBe] === 'Yes';
      }
    }

    const message = result?.message || 'In Stock';

    return message !== 'Out of stock';
  }

  async validateQuantity(event) {
    const inputValue = parseInt(event.target.value, 10);
    const { index, sku } = event.target.dataset;
    const style = this.getTextStyle(event);
    const deliveryAPI = SDG.Data.deliveryAPI || null;

    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = t('sections.quick_order_list.min_error', { min: event.target.dataset.min });
    } else if (inputValue > parseInt(event.target.max, 10)) {
      message = t('sections.quick_order_list.max_error', { max: event.target.max });
    } else if (inputValue % parseInt(event.target.step, 10) !== 0) {
      message = t('sections.quick_order_list.step_error', { step: event.target.step });
    }

    if (message) {
      this.setValidity(event, index, message);
      return;
    }

    // Only an increase can push a line past what is sellable. Never block a decrease, so a
    // customer whose line is already over the limit can still reduce it.
    const currentQty = parseInt(event.target.getAttribute('value'), 10);
    const isIncrease = Number.isNaN(currentQty) || inputValue > currentQty;

    if (deliveryAPI && sku && isIncrease) {
      try {
        // Each line is validated against ITS pool. A trapunto line: the API is asked about
        // the whole trapunto position (this line + the other trapunto lines of the SKU) so
        // `can_be_trapunto` judges it against QD-Semi capacity. Any other line: the API is
        // asked about THIS line's qty only (inflating it made can_be_trapunto always No), and
        // the other non-trapunto lines are added client-side for the total_qty comparison.
        const isTrapuntoQtyLine = this.isTrapuntoStyle(style);
        const otherLinesQty = this.getOtherLinesQty(event.target, sku, isTrapuntoQtyLine);
        const skuTotalQty = inputValue + otherLinesQty;
        const response = await fetch(deliveryAPI, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sku,
            qty: isTrapuntoQtyLine ? skuTotalQty : inputValue,
            country: SDG?.Data?.country || sessionStorage?.getItem('_locale_code') || 'US',
          }),
        });

        const result = await response.json();

        if (!this.validateQuantityApi(style, result, skuTotalQty)) {
          const sellableQty = this.getSellableQty(result);
          const remainingQty = sellableQty === null
            ? null
            : Math.max(0, sellableQty - otherLinesQty);
          // Trapunto is decided by can_be_trapunto, not total_qty — no remaining count to show.
          const overRemaining = !isTrapuntoQtyLine && remainingQty !== null
            && remainingQty > 0 && inputValue > remainingQty;
          // TODO: move the fallback errorMessage to setting json
          let errorMessage = 'This item has a maximum quantity limit.';
          if (overRemaining) {
            errorMessage = t('sections.cart.cart_quantity_error_html', { quantity: remainingQty });
          } else if (isTrapuntoQtyLine) {
            errorMessage = t('sections.cart.cart_add_max_error');
          }
          this.setValidity(event, index, errorMessage);
          return;
        }
        // TODO: update '_lead_time_message' into cart item properties
      } catch (err) {
        // Fail closed: never call Shopify's qty update when the qty check could not run.
        console.error('API validation failed', err);
        this.setValidity(event, index, t('sections.cart.cart_error'));
        return;
      }
    }

    this.setLineError(index, '');
    event.target.setCustomValidity('');
    event.target.reportValidity();
    this.updateQuantity(
      index,
      inputValue,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId,
    );
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.cleanupDuplicateHeaders();
          if (this.cartItems?.length > 0) {
            this.cartItems?.forEach((item) => {
              this.initAccordion(item);
            });
          }
          // Fetch delivery messages after an external cart update (e.g. add-to-cart).
          this.updateCartItemsDeliveryMessage(true);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.cleanupDuplicateHeaders();
          if (this.cartItems?.length > 0) {
            this.cartItems?.forEach((item) => {
              this.initAccordion(item);
            });
          }
          // Fetch delivery messages after an external cart update (e.g. add-to-cart).
          this.updateCartItemsDeliveryMessage(true);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble-mobile',
        section: 'cart-icon-bubble-mobile',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  // Qty Shopify currently holds for this line (by 1-based index, or by item key). null if unknown.
  async getCartLineQty(line) {
    try {
      const { items } = await (await fetch(`${routes.cart_url}.js`)).json();
      const item = String(line).length > 10 ? items.find((i) => i.key === line) : items[line - 1];
      return item ? item.quantity : null;
    } catch (e) {
      return null;
    }
  }

  async updateQuantity(line, quantity, name, variantId) {
    const template = SDG?.Data?.template || null;
    this.enableLoading(line);

    const param = {
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    // update function to accept item key
    if (line.length > 10) {
      param.id = line;
      param.line = null;
    }

    const body = JSON.stringify(param);

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        // Shopify 422 still carries {"errors": "..."} (e.g. stock limit); let the errors branch below show it.
        if (!response.ok && response.status !== 422) {
          throw new Error(`Server returned ${response.status}: ${response.statusText}`);
        }
        return response.text();
      })
      .then(async (state) => {
        let parsedState;
        try {
          parsedState = JSON.parse(state);
        } catch (e) {
          throw new Error(`Failed to parse server response: ${e.message}`);
        }

        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          // 422 with a lower kept qty (stock limit): re-send it so the success path re-renders.
          const priorQty = parseInt(quantityElement?.getAttribute('value'), 10);
          const keptQty = await this.getCartLineQty(line);
          // `keptQty !== quantity`: no endless re-send if the kept qty itself keeps returning 422.
          if (keptQty !== null && keptQty !== priorQty && keptQty !== quantity) {
            this.updateQuantity(line, keptQty, name, variantId);
            return;
          }

          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }

          // Max qty: always "You can only add N of this item" (not Shopify's native 422 text).
          const errorMessage = keptQty !== null && keptQty < quantity
            ? t('sections.cart.cart_quantity_error_html', { quantity: keptQty })
            : parsedState.errors;

          this.updateLiveRegions(line, errorMessage);

          // The inline line error already shows it; use the general block only as a fallback.
          const hasLineError = document.getElementById(`Line-item-error-${line}`);
          const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
          if (errors && !hasLineError) errors.textContent = typeof errorMessage === 'string'
            ? errorMessage
            : t('sections.cart.cart_error');

          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          if (section.id === 'main-cart-footer') {
            const htmlElement = new DOMParser().parseFromString(
              parsedState.sections[section.section],
              'text/html'
            );

            const newCartBlocks = htmlElement.querySelector('.cart__blocks');

            const existingCartBlocks = document.querySelector('.cart__blocks');

            if (newCartBlocks && existingCartBlocks) {
              existingCartBlocks.innerHTML = newCartBlocks.innerHTML;
            } else {
              console.warn('Could not find cart__blocks element in either the response or current DOM');

              const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
              const newElement = htmlElement.getElementById(section.id);

              if (elementToReplace && newElement) {
                elementToReplace.innerHTML = newElement.innerHTML;
              }
            }
          } else {
            const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
            if (!elementToReplace) {
              console.warn(`Element to replace with ID "${section.id}" was not found in the DOM`);
              return;
            }

            const htmlElement = new DOMParser().parseFromString(
              parsedState.sections[section.section],
              'text/html'
            );

            const newElement = htmlElement.getElementById(section.id);
            if (!newElement) {
              console.warn(`Element with ID "${section.id}" was not found in the response HTML`);
              return;
            }

            elementToReplace.innerHTML = newElement.innerHTML;
          }
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement?.value)) {
          if (typeof updatedValue === 'undefined') {
            message = t('sections.cart.cart_error');
          } else {
            message = t('sections.cart.cart_quantity_error_html', { quantity: updatedValue });
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
      })
      .catch((error) => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');

        if (errors) {
          errors.textContent = `${t('sections.cart.cart_error') || 'Error updating cart'}. ${error.message || ''}`;
        }

        console.error('Cart update error:', error);
      })
      .finally(() => {
        if (line) this.disableLoading(line);

        if (template === 'cart') {
          this.cleanupDuplicateHeaders();
          this.initAccordion();
        }
        // Fetch delivery messages on quantity change / remove (drawer + cart page).
        this.updateCartItemsDeliveryMessage(true);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

      // remove error message after 3 seconds
      setTimeout(() => {
        lineItemError.querySelector('.cart-item__error-text').innerHTML = '';
      }, 3000);
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      if (cartStatus) cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');
    let item;
    if (line?.length > 10) {
      item = this.querySelector(`[data-item-key="${line}"]`);
    } else {
      item = this.querySelector(`#CartItem-${line}`) || this.querySelector(`#CartDrawer-Item-${line}`);
    }
    if (item) {
      const $cartItem = item.querySelector('.js-cart-item');
      if ($cartItem) $cartItem.classList.add('is-loading');
    }

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems') || null;
    if (!mainCartItems) return;

    mainCartItems.classList.remove('cart__items--disabled');
    let item;
    if (line?.length > 10) {
      item = this.querySelector(`[data-item-key="${line}"]`);
    } else {
      item = this.querySelector(`#CartItem-${line}`) || this.querySelector(`#CartDrawer-Item-${line}`);
    }
    if (item) {
      const $cartItem = item.querySelector('.js-cart-item');
      if ($cartItem) $cartItem.classList.remove('is-loading');
    }
  }

  enableCheckoutButton() {
    const checkoutButton = document.getElementById(this.config.dom.cart_checkout_button);
    if (checkoutButton) checkoutButton.disabled = false;
  }

  disableCheckoutButton() {
    const checkoutButton = document.getElementById(this.config.dom.cart_checkout_button);
    if (checkoutButton) checkoutButton.disabled = true;
  }

  initAccordion(line) {
    const template = SDG?.Data?.template || null;

    if (line instanceof HTMLElement) {
      const $acc = line.querySelector(this.config.dom.cart_item_acc) || null;
      const isInitialized = line.getAttribute('data-accordion-initialized') === 'true';

      if (!$acc || !$acc?.id || isInitialized) return;

      const accordionOptions = {
        dom: {
          id: $acc.id,
          btn: 'js-cart-item-acc-btn',
          menu: 'js-cart-item-acc-menu',
        },
        enable_linking: true,
      };
      const accordion = SDG?.accordion(accordionOptions);

      if (accordion) {
        accordion?.init();
        line.setAttribute('data-accordion-initialized', 'true');
      }
      return;
    }

    // fallback for cart items re-render
    if (template === 'cart') {
      this?.cartItems?.forEach((item) => {
        const $acc = item.querySelector(this.config.dom.cart_item_acc) || null;

        if (!$acc || !$acc?.id) return;

        const accordionOptions = {
          dom: {
            id: $acc.id,
            btn: 'js-cart-item-acc-btn',
            menu: 'js-cart-item-acc-menu',
          },
          enable_linking: true,
        };
        const accordion = SDG?.accordion(accordionOptions);

        if (accordion) {
          accordion?.init();
          item.setAttribute('data-accordion-initialized', 'true');
        }
      });
    }
  }

  async updateCartItemsDeliveryMessage(allowFetch = true) {
    try {
      const utils = SDG?.cartUtils() || {};

      if (utils) {
        utils?.getCartItemsDeliveryMessage(allowFetch);
      }
    } catch (err) {
      console.error({ message: 'Error fetching delivery data', error: err });
    }
  }

  cleanupDuplicateHeaders() {
    // Find all tbody elements
    const tbodies = this.querySelectorAll('tbody.js-contents');

    tbodies.forEach(tbody => {
      // Find any rows that contain th elements (header rows)
      const headerRows = tbody.querySelectorAll('tr:not([id]):has(th.cart-items__heading)');

      // Remove these duplicate header rows
      headerRows.forEach(row => {
        row.remove();
      });
    });
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
